import React from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiEye, FiEdit2 } from 'react-icons/fi';
import dayjs from 'dayjs';
import { SaktiPaginationMeta, SaktiSpbListItem } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

interface SaktiSpbTableProps {
  loading: boolean;
  rows: SaktiSpbListItem[];
  pagination: SaktiPaginationMeta;
  onPageChange: (page: number) => void;
  onViewDetail: (id: string) => void;
  onEdit: (id: string) => void;
}

const SaktiSpbTable: React.FC<SaktiSpbTableProps> = ({
  loading,
  rows,
  pagination,
  onPageChange,
  onViewDetail,
  onEdit,
}) => {
  return (
    <VStack align="stretch" spacing={4}>
      <Skeleton isLoaded={!loading} borderRadius={SAKTI_UI_SIZE.cardRadius}>
        <Box borderRadius={SAKTI_UI_SIZE.cardRadius} overflow="hidden" borderWidth="1px" borderColor="gray.200" bg="white">
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg="customGreen.500">
                <Tr>
                  <Th color="white">SPB No.</Th>
                  <Th color="white">Estate / Divisi</Th>
                  <Th color="white">Kendaraan</Th>
                  <Th color="white">Driver</Th>
                  <Th color="white" isNumeric>Jjg</Th>
                  <Th color="white" isNumeric>Brondolan</Th>
                  <Th color="white" isNumeric>Berat (kg)</Th>
                  <Th color="white">Tipe</Th>
                  <Th color="white">Aksi</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.length === 0 ? (
                  <Tr><Td colSpan={10}><Text color="gray.500" textAlign="center" py={4}>Tidak ada data</Text></Td></Tr>
                ) : (
                  rows.map((s) => (
                    <Tr key={s.id}>
                      <Td><Text fontWeight="bold">{s.transport_nbr}</Text><Text fontSize="xs" color="gray.500">{dayjs(s.transport_date).format('DD/MM/YYYY')}</Text></Td>
                      <Td><Text>{s.estate_name}</Text><Text fontSize="xs" color="gray.500">{s.division_name}</Text></Td>
                      <Td><Text fontFamily="mono">{s.equipment_code}</Text></Td>
                      <Td>{s.driver_name}</Td>
                      <Td isNumeric>{s.total_bunch}</Td>
                      <Td isNumeric>{s.total_loose_fruit.toFixed(1)}</Td>
                      <Td isNumeric><strong>{s.total_weight.toLocaleString('id-ID')}</strong></Td>
                      <Td>{s.is_external ? <Badge colorScheme="orange">External</Badge> : <Badge colorScheme="blue">Internal</Badge>}</Td>
                      <Td>
                        <HStack spacing={1}>
                          <IconButton size="xs" icon={<FiEye />} aria-label="View" onClick={() => onViewDetail(s.id)} />
                          <IconButton size="xs" icon={<FiEdit2 />} aria-label="Edit" onClick={() => onEdit(s.id)} />
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
          <HStack px={4} py={3} bg="customGreen.500" color="white" justify="space-between">
            <Text fontSize="xs" fontWeight="semibold">TOTAL {pagination.total_items.toLocaleString('id-ID')} SPB</Text>
            <HStack spacing={1}>
              <IconButton aria-label="Prev" icon={<FiChevronLeft />} size="sm" variant="outline" isDisabled={pagination.page <= 1} onClick={() => onPageChange(pagination.page - 1)} />
              <Button size="sm" variant="solid" bg="white" color="customGreen.500">{pagination.page}</Button>
              <IconButton aria-label="Next" icon={<FiChevronRight />} size="sm" variant="outline" isDisabled={pagination.page >= pagination.total_pages} onClick={() => onPageChange(pagination.page + 1)} />
            </HStack>
          </HStack>
        </Box>
      </Skeleton>
    </VStack>
  );
};

export default SaktiSpbTable;
